import React, { useState } from 'react'
import {SlidersHorizontal, X} from 'lucide-react'

interface AdvancedFiltersProps {
  onFilterChange: (filters: {
    priceRange: [number, number]
    minRating: number
    availability: string[]
    pricingModel: string[]
  }) => void
  isOpen: boolean
  onClose: () => void
}

export function AdvancedFilters({ onFilterChange, isOpen, onClose }: AdvancedFiltersProps) {
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500])
  const [minRating, setMinRating] = useState(0)
  const [availability, setAvailability] = useState<string[]>([])
  const [pricingModel, setPricingModel] = useState<string[]>([])

  const handleApply = () => {
    onFilterChange({
      priceRange,
      minRating,
      availability,
      pricingModel
    })
    onClose()
  }

  const handleReset = () => {
    setPriceRange([0, 500])
    setMinRating(0)
    setAvailability([])
    setPricingModel([])
    onFilterChange({
      priceRange: [0, 500],
      minRating: 0,
      availability: [],
      pricingModel: []
    })
  }

  const toggleArrayFilter = (array: string[], setArray: (val: string[]) => void, value: string) => {
    if (array.includes(value)) {
      setArray(array.filter(v => v !== value))
    } else {
      setArray([...array, value])
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SlidersHorizontal className="w-5 h-5 text-electric-purple-400" />
            <h2 className="text-2xl font-bold text-white">Advanced Filters</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Price Range */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3">
              Price Range: ${priceRange[0]} - ${priceRange[1]}
            </label>
            <div className="space-y-2">
              <input
                type="range"
                min="0"
                max="500"
                value={priceRange[0]}
                onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-electric-purple-500"
              />
              <input
                type="range"
                min="0"
                max="500"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-electric-purple-500"
              />
            </div>
          </div>

          {/* Minimum Rating */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3">
              Minimum Rating: {minRating} ★
            </label>
            <input
              type="range"
              min="0"
              max="5"
              step="0.5"
              value={minRating}
              onChange={(e) => setMinRating(parseFloat(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-electric-purple-500"
            />
          </div>

          {/* Availability */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3">Availability</label>
            <div className="flex gap-2 flex-wrap">
              {['online', 'busy', 'offline'].map((status) => (
                <button
                  key={status}
                  onClick={() => toggleArrayFilter(availability, setAvailability, status)}
                  className={`px-4 py-2 rounded-lg capitalize transition-all ${
                    availability.includes(status)
                      ? 'bg-electric-purple-500 text-white'
                      : 'bg-slate-800 text-slate-300 border border-slate-700'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Model */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3">Pricing Model</label>
            <div className="flex gap-2 flex-wrap">
              {['per_task', 'per_hour', 'subscription'].map((model) => (
                <button
                  key={model}
                  onClick={() => toggleArrayFilter(pricingModel, setPricingModel, model)}
                  className={`px-4 py-2 rounded-lg capitalize transition-all ${
                    pricingModel.includes(model)
                      ? 'bg-electric-purple-500 text-white'
                      : 'bg-slate-800 text-slate-300 border border-slate-700'
                  }`}
                >
                  {model.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 p-6 flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 px-6 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-700 transition-all"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  )
}
